import _ from 'lodash';

import { getDomain } from './formatting';
import { SKIP_REASONS, getLinkCardActions } from './links_to_check_helpers';
import { formatPrice } from './url_data_agent_live_matches_helpers';

export const UNDO_WINDOW_MS = 5000;

/**
 * Holds one answer for the undo window before sending it. A second answer sends the held one at
 * once, undo drops it unsent, and flush sends it now (the page is being hidden or left).
 * @param {{ delayMs?: number, onSend: (answer) => void, onChange: (heldAnswer) => void }} options
 */
export const createAnswerWindow = ({ delayMs = UNDO_WINDOW_MS, onSend, onChange }) => {
  let held = null;
  let timer = null;

  const release = () => {
    clearTimeout(timer);
    const answer = held;
    held = null;
    onChange(null);
    return answer;
  };

  const flush = () => {
    if (held) onSend(release());
  };

  return {
    hold: answer => {
      flush();
      held = answer;
      onChange(answer);
      timer = setTimeout(flush, delayMs);
    },
    undo: () => (held ? release() : null),
    flush
  };
};

/**
 * The PUT /api/UrlData/:hashedUrlStem body for an answer. Every answer records that our pick was
 * on screen; a skip also records why.
 */
export const getAnswerUpdate = ({ kind, product, metadata = {}, reason, note }, now = Date.now()) => {
  if (kind === 'skip') return { dismissedAt: now, metadata: { humanCatalogAction: 'dismiss_url', dismissReason: reason, agentSuggestionShown: true } };
  if (kind === 'help') return { escalatedAt: now, escalationData: { reason: note }, metadata: { humanCatalogAction: 'escalate', agentSuggestionShown: true } };
  return {
    Product_id: product.id,
    metadata: { ...metadata, humanCatalogAction: metadata.userCreatedProduct ? 'create_product' : 'attach_existing_product', agentSuggestionShown: true }
  };
};

export const getAnswerLabel = ({ kind, product, metadata = {}, reason }, link) => {
  if (kind === 'skip') return `Skipped: ${_.find(SKIP_REASONS, { dismissReason: reason }).display.toLowerCase()} (${link.domain})`;
  if (kind === 'help') return `Asked for help on ${link.domain}`;
  if (kind === 'alert_right') return 'Marked right. The alert is cleared.';
  if (kind === 'alert_repoint') {
    return `${link.applied.action === 'create' ? 'Merged into' : 'Fixed our link:'} ${_.compact([product.brand, product.title]).join(' · ')}`;
  }
  if (kind === 'merge') return `Merged into ${_.compact([link.pair.keep.brand, link.pair.keep.title]).join(' · ')}`;
  if (kind === 'keep_both') return 'Kept both. We won’t flag this pair again.';
  if (metadata.userCreatedProduct) return `Added “${product.title}” and linked it`;
  return `Linked to ${_.compact([product.brand, product.title]).join(' · ')}`;
};

export const getAnswerFailure = ({ kind, args, link }) => {
  if (kind === 'skip') return `Couldn’t save the skip on ${link.domain}.`;
  if (kind === 'help') return `Couldn’t send ${link.domain} to the team lead.`;
  if (kind === 'alert_right') return `Couldn’t save that our ${link.domain} link looks right.`;
  if (kind === 'alert_repoint') return `Couldn’t ${link.applied.action === 'create' ? 'merge into' : 'point our link at'} ${args.product.title}.`;
  if (kind === 'merge') return `Couldn’t merge into ${link.pair.keep.title}.`;
  if (kind === 'keep_both') return 'Couldn’t keep both products.';
  return `Couldn’t save the link to ${args.product.title}.`;
};

// The site's rules never match the pick's brand, so the save would reject any product of that brand.
const isPickBrandBlocked = link => link.holdReason === 'suppressed_pair';

const isSameBrand = (product, other) =>
  product.all_brand?.id && other?.all_brand?.id ? product.all_brand.id === other.all_brand.id : _.toLower(product.brand) === _.toLower(other?.brand);

/** The card's buttons that can be answered: none that links a product the save would reject. */
export const getAnswerableActions = link => getLinkCardActions(link).filter(action => !(action.key === 'link' && isPickBrandBlocked(link)));

/** The products the number keys link, in the order the card shows them; on an alert card, the run's other close matches. */
export const getLinkChoices = link => {
  if (link?.cardType === 'alert') return (link.candidates || []).filter(product => product.id !== link.applied?.product?.id).slice(0, 2);
  if (link?.cardType !== 'decide') return [];
  if (link.question === 'same_product') {
    const choices = _.uniqBy(_.compact([link.pick, ...(link.candidates || [])]), 'id');
    return (isPickBrandBlocked(link) ? choices.filter(product => !isSameBrand(product, link.pick)) : choices).slice(0, 3);
  }
  if (link.question === 'new_product') return (link.similarInCatalog || []).slice(0, 2);
  return [];
};

/** Names the number keys beside the buttons: after the dark button, or first when there is none. */
export const getChoicesHint = link => {
  const keys = getLinkChoices(link).map((product, index) => String(index + 1));
  if (link.question === 'same_product' && link.pick && !isPickBrandBlocked(link)) return keys.length > 1 ? { before: 'or', keys: keys.slice(1), after: 'for another' } : null;
  if (link.question === 'same_product') return keys.length ? { before: 'Pick a match:', keys } : null;
  if (link.question === 'new_product' && keys.length) return { before: 'or', keys, after: keys.length > 1 ? 'to link a close match' : 'to link the close match' };
  return null;
};

const ANY_CARD_KEYS = { o: 'open_page', p: 'open_pick', ArrowRight: 'not_now' };
const LINK_CARD_KEYS = { c: 'search', n: 'create', s: 'skip', h: 'help' };

/**
 * What a key does on this card: A is whatever the dark button does, and opening and Not now work
 * on every card. Returns the card's own button when it has one, so a skip button keeps its reason.
 * On a Likely wrong card every button answers to the key it shows.
 */
export const getKeyAction = (link, key) => {
  const actions = getAnswerableActions(link);
  const ownButton = link.cardType !== 'decide' && actions.find(action => action.shortcut.toLowerCase() === key);
  if (ownButton) return ownButton;
  if (key === 'a') return link.cardType === 'decide' ? actions.find(action => action.primary) || null : null;
  const actionKey = ANY_CARD_KEYS[key] || (link.cardType === 'decide' && LINK_CARD_KEYS[key]);
  if (!actionKey) return null;
  return actions.find(action => action.key === actionKey && !action.dismissReason) || { key: actionKey };
};

/**
 * Counts as the page shows them. The counts route answers from a shared cache, so the page takes off
 * its own answers the cache may not have seen: any not sent yet, and any sent within the cache's age.
 */
export const getCountsWithoutRecentAnswers = (counts, answers, { cacheMs, now = Date.now() }) =>
  answers
    .filter(answer => counts[answer.question] && (!answer.sentAt || now - answer.sentAt < cacheMs))
    .reduce((shown, answer) => ({ ...shown, [answer.question]: { ...shown[answer.question], waiting: Math.max(0, shown[answer.question].waiting - 1) } }), counts);

export const isTypingTarget = target => ['INPUT', 'TEXTAREA', 'SELECT'].includes(target?.tagName) || Boolean(target?.isContentEditable);

export const getTodayEntries = (history, sessionEntries) =>
  _.orderBy(
    [
      ...sessionEntries,
      ...history.map(item => ({ key: `match-${item.id}`, label: `Linked to ${_.compact([item.brandName, item.productTitle]).join(' · ')}`, at: item.matchedAt, hashedUrlStem: item.hashedUrlStem }))
    ],
    'at',
    'desc'
  );

// The seed length URL Data's product search uses
const SEARCH_SEED_NAME_WORDS = 3;

export const getLinkSearchSeed = link => {
  const { page, proposal, linkWords } = link;
  const brand = page?.brand || proposal?.brand || '';
  const nameWords =
    page?.readable && page.title
      ? page.title
          .trim()
          .split(' ')
          .slice(0, SEARCH_SEED_NAME_WORDS)
      : (linkWords || '').split(' ');
  return _.uniqBy(_.compact([...brand.split(' '), ...nameWords]), _.toLower).join(' ');
};

export const getSearchResultLine = product =>
  _.compact([product.brand, formatPrice(product.fallbackPrice), product.fallbackUrl && `listed from ${getDomain(product.fallbackUrl)}`]).join(' · ');

export const getLinkCreateSuggestions = link => {
  const { page, proposal, creatorPin, linkWords } = link;
  return {
    title: proposal?.title || (page?.readable ? page.title : null) || linkWords,
    brandName: proposal?.brand || page?.brand || null,
    categoryId: proposal?.categoryId || null,
    images: getLinkCreateImages({ page, proposal, creatorPin })
  };
};

const getLinkCreateImages = ({ page, proposal, creatorPin }) => {
  if (proposal?.images?.length) return proposal.images;
  if (page?.images?.length) return page.images;
  return _.compact([proposal?.image || page?.image || creatorPin?.image]);
};
